import { useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router'
import { Form, message } from 'antd'
import { SwtchError } from '../models/error'
import { ListingFull, ListingStatusUpdate } from '../models/listing'
import { GetListing, UpdateListing, UpdateListingsStatus } from '../services/data-provider/listing'
import { useFormatMessage } from '../helpers/intl'
import { addQueryString } from '../helpers/url'
import { Links } from '../components/routes/paths'
import { useTagging } from './useTagging'
import moment from 'moment'
import { useSelector } from './useSelector'

export const useListing = () => {
  // @ts-ignore
  const { listingId } = useParams()
  const history = useHistory()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<SwtchError>()
  const [listing, setListing] = useState<ListingFull>()
  const [form] = Form.useForm()

  const [updatedServiceStatusBody, setUpdatedServiceStatusBody] = useState<ListingStatusUpdate | {}>({})

  const successEditListingText = useFormatMessage('dashboard.updated.listing', 'Listing has been updated successfully')

  const { applyTagToObject, getTaggingsWithoutPagination, taggings, setTaggings, dropdownRender } = useTagging()
  const { siteLabelCleaner } = useSelector()

  useEffect(() => {
    fetchListing()
    getTaggingsWithoutPagination('Listing')
  }, [])

  const initialValues = initializingValues(listing, siteLabelCleaner)

  const fetchListing = () => {
    setError(undefined)
    setLoading(true)
    GetListing(listingId)
      .then((resp) => {
        setListing(resp)
        const values = initializingValues(resp, siteLabelCleaner)
        form.setFieldsValue(values)
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false))
  }

  const applyTag = () => {
    applyTagToObject([{ id: parseFloat(listingId) }], 'listings', true)
      .then(() => {
        setTimeout(() => {
          setLoading(false)
          history.push(addQueryString(Links.chargers(), {}))
        }, 2000)
      })
      .catch((err) => setError(err))
  }

  const onFinish = () => {
    setError(undefined)
    setLoading(true)
    UpdateListing(listingId, {
      id: listingId,
      authorId: form.getFieldValue('authorId'),
      title: form.getFieldValue('title'),
      additionalServiceFee: form.getFieldValue('additionalServiceFee'),
      trailerAccessible: form.getFieldValue('trailerAccessible'),
      fundingSources: form.getFieldValue('fundingSources'),
      commisionPercent:
        form.getFieldValue('commisionPercent') === '2.9% + 30c'
          ? 0.0
          : form.getFieldValue('customComissionFee') ?? form.getFieldValue('commisionPercent'),
      carDetailRequired: form.getFieldValue('carDetailRequired'),
      customNotification: form.getFieldValue('customNotification'),
      output: form.getFieldValue('output'),
      tenantId: `${form.getFieldValue('tenantId')}`,
      kind: form.getFieldValue('kind'),
      paymentType: form.getFieldValue('paymentType'),
      hidden: form.getFieldValue('hidden'),
      stripeAccountId: form.getFieldValue('stripeAccountId'),
      description: form.getFieldValue('description'),
      enableLoadManagementAutoStop: form.getFieldValue('enableLoadManagementAutoStop'),
      addCloudLoadManagement: form.getFieldValue('addCloudLoadManagement'),
      paymentTerminal: form.getFieldValue('paymentTerminal'),
      paymentTerminalSerialNumber: form.getFieldValue('paymentTerminalSerialNumber'),
      payterSerialNumber: form.getFieldValue('payterSerialNumber'),
      displayName: form.getFieldValue('displayName'),
      imageFileName: form.getFieldValue('imageFileName'),
      imageObjectKey: form.getFieldValue('imageObjectKey'),
      chargingType: form.getFieldValue('chargerType'),
    } as any)
      .then(() => {
        fetchListing()
        if (Object.keys(updatedServiceStatusBody).length) {
          UpdateListingsStatus({
            ...(updatedServiceStatusBody as ListingStatusUpdate),
            listing_ids: [listingId],
            previous_status: listing?.connector?.serviceStatus as string,
            status_changed_since: moment((updatedServiceStatusBody as ListingStatusUpdate)?.status_changed_since)
              .utc()
              .toISOString(),
            resolution_id: (updatedServiceStatusBody as ListingStatusUpdate)?.resolution_id ?? null,
          })
            .then((resp) => {
              applyTag()
            })
            .catch((err) => setError(err))
            .finally(() => setLoading(false))
        } else {
          applyTag()
        }

        message.success(successEditListingText)
      })
      .catch((err) => setError(err))
      .finally(() => setLoading(false))
  }

  const listingTaggings = (listing as any)?.taggings || []

  return {
    loading,
    error,
    setError,
    listing,
    initialValues,
    form,
    onFinish,
    fetchListing,
    taggings,
    setTaggings,
    listingId,
    dropdownRender,
    setUpdatedServiceStatusBody,
    listingTaggings,
  }
}

const initializingValues = (listing: ListingFull | undefined, siteLabelCleaner: (value: string) => string) => {
  // Get chargerType from listing's charger object (API may return charger even though it's not in TypeScript interface)
  const listingCharger = (listing as any)?.charger
  const chargerType = listingCharger?.chargingType || undefined

  return {
    title: listing?.title,
    description: listing?.description,
    kind: listing?.kind,
    paymentType: listing?.paymentType,
    fundingSources: listing?.fundingSources,
    trailerAccessible: listing?.trailerAccessible,
    serviceStatus: listing?.connector?.serviceStatus,
    reason: listing?.reason,

    // author
    authorId: listing?.authorId,
    listingAuthor: listing?.person?.name,
    stripeAccountId: listing?.stripeAccountId,
    // stripeAccount: listing?.stripeAccount,
    stripeAccountName: listing?.stripeAccount?.name,
    // author: listing?.author,

    // map & access
    hidden: listing?.hidden,
    carDetailRequired: listing?.carDetailRequired,

    // charger
    tenantId: listing?.tenantId,
    // tenant: listing?.tenant,
    tenantName: siteLabelCleaner(
      listing?.tenant?.combineName || listing?.tenant?.displayName || listing?.tenant?.name || '',
    ),
    output: listing?.output,
    customNotification: listing?.customNotification,
    chargerType: chargerType,

    // commmission & fee
    commisionPercent:
      listing?.additionalServiceFee === 'on' || listing?.additionalServiceFee === '1'
        ? '2.9% + 30c'
        : listing?.commisionPercent,
    additionalServiceFee: listing?.additionalServiceFee,
    customComissionFee: listing?.commisionPercent,
    addCloudLoadManagement: listing?.addCloudLoadManagement,
    enableLoadManagementAutoStop: listing?.enableLoadManagementAutoStop,
    paymentTerminal: listing?.paymentTerminal,
    paymentTerminalSerialNumber: listing?.paymentTerminalSerialNumber,
    payterSerialNumber: listing?.payterSerialNumber,
    displayName: listing?.displayName,
    imageFileName: listing?.imageFileName,
    imageObjectKey: listing?.imageObjectKey,
  }
}
